/**
 * 2048 PK 双盘物理坐标渲染器 (Dual-Board Tile Renderer)
 * 职责：
 * 1. 本地玩家 4x4 高清平滑滑移动画与合并数字渲染
 * 2. 对手 4x4 全息微缩镜像盘实时同步与映射
 * 3. 内存级网格物理绝对坐标缓存，彻底杜绝运行期 Forced Reflow
 */

import type { Tile } from "../game/boardEngine";
import type { TileData } from "../../shared/protocol";

export class LocalTileRenderer {
  private container: HTMLElement;
  private gridCells: HTMLElement[];
  private coordsCache: { x: number; y: number }[] = [];

  constructor(container: HTMLElement, gridCells: HTMLElement[]) {
    this.container = container;
    this.gridCells = gridCells;
    this.updateCoordsCache();
  }

  public updateCoordsCache() {
    const firstCell = this.gridCells[0];
    if (firstCell && firstCell.offsetWidth > 0) {
      this.container.style.setProperty("--cell-size", `${firstCell.offsetWidth}px`);
    }
    this.coordsCache = this.gridCells.map((cell) => {
      if (!cell) return { x: 0, y: 0 };
      return { x: cell.offsetLeft, y: cell.offsetTop };
    });
  }

  public getCellCoords(r: number, c: number): { x: number; y: number } {
    const idx = r * 4 + c;
    return this.coordsCache[idx] || { x: 0, y: 0 };
  }

  public updateTilePosition(tile: Tile) {
    if (!tile.el) return;
    const { x, y } = this.getCellCoords(tile.r, tile.c);
    tile.el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }

  public mountTile(tile: Tile, extraClass = ""): HTMLElement {
    this.updateCoordsCache();
    const el = document.createElement("div");
    el.className = `tile${tile.isNew ? " tile-new" : ""}${tile.isMerged ? " tile-merged" : ""}${
      tile.isPendingEject ? " tile-pending-eject" : ""
    }${extraClass ? ` ${extraClass}` : ""}`;
    tile.el = el;
    this.updateTilePosition(tile);

    const inner = document.createElement("div");
    const valClass = tile.val <= 2048 ? `tile-${tile.val}` : "tile-super";
    inner.className = `tile-inner ${valClass}`;
    inner.textContent = String(tile.val);
    el.appendChild(inner);

    if (tile.isMerged) {
      const badge = document.createElement("div");
      badge.className = "tile-popup-score";
      badge.textContent = `+${tile.val}`;
      el.appendChild(badge);
      badge.addEventListener("animationend", () => {
        if (badge.parentNode) badge.parentNode.removeChild(badge);
      });
    }

    this.container.appendChild(el);
    return el;
  }

  public setTilePendingEject(tile: Tile, pending: boolean) {
    tile.isPendingEject = pending;
    if (tile.el) {
      tile.el.classList.toggle("tile-pending-eject", pending);
    }
  }

  public refreshTileVisual(tile: Tile, pulseClass?: string) {
    if (!tile.el) return;
    const el = tile.el;
    const inner = el.querySelector(".tile-inner") as HTMLElement;
    if (!inner) return;

    const valClass = tile.val <= 2048 ? `tile-${tile.val}` : "tile-super";
    inner.className = `tile-inner ${valClass}`;
    inner.textContent = String(tile.val);

    if (pulseClass) {
      el.classList.remove(pulseClass);
      void el.offsetWidth;
      el.classList.add(pulseClass);
      setTimeout(() => {
        el.classList.remove(pulseClass);
      }, 900);
    }
  }

  public showTileBadgePopup(tile: Tile, text: string) {
    if (!tile.el) return;
    const drainEl = document.createElement("div");
    drainEl.className = "tile-drain-score";
    drainEl.textContent = text;
    tile.el.appendChild(drainEl);
    setTimeout(() => {
      if (drainEl.parentNode) drainEl.parentNode.removeChild(drainEl);
    }, 950);
  }

  /**
   * 真实对角利刃劈砍 + 方块一分为二撕裂特效 (True Diagonal Blade Slash & Tear-Apart)
   * 原方块沿对角线被刀光劈开，左上与右下两半带原数字向两侧撕裂飞散，露出底层的减半新数字！
   */
  public playSlashAndSplitEffect(tile: Tile, oldVal: number, newVal: number) {
    if (!tile.el) return;
    const el = tile.el;

    // 1. 先将底层真实方块更新为减半后的新数值
    tile.val = newVal;
    this.refreshTileVisual(tile, "tile-downgraded-pulse");

    // 2. 在方块上方挂载一分为二撕裂图层与刀锋光痕
    mountTileSplitOverlay(el, oldVal);

    // 3. 弹出直观降阶浮标
    this.showTileBadgePopup(tile, `${oldVal} ➔ ${newVal}`);
  }

  public removeTile(tile: Tile) {
    if (tile.el && tile.el.parentNode) {
      tile.el.parentNode.removeChild(tile.el);
    }
  }

  public repositionAll(tiles: Tile[]) {
    this.updateCoordsCache();
    for (const t of tiles) {
      this.updateTilePosition(t);
    }
  }

  public clearAll() {
    this.container.innerHTML = "";
  }
}

/**
 * 对手微缩全息镜像渲染器 (含实时物理平滑滑移与合并弹性动效)
 */
export class RivalTileRenderer {
  private container: HTMLElement;
  private gridCells: HTMLElement[];
  private coordsCache: { x: number; y: number }[] = [];
  private tileMap: Map<number, { el: HTMLElement; val: number }> = new Map();
  private pendingSettleTimer: number | null = null;
  private pendingSettleFn: (() => void) | null = null;

  constructor(container: HTMLElement, gridCells: HTMLElement[]) {
    this.container = container;
    this.gridCells = gridCells;
    this.updateCoordsCache();
  }

  public updateCoordsCache() {
    const firstCell = this.gridCells[0];
    if (firstCell && firstCell.offsetWidth > 0) {
      this.container.style.setProperty("--cell-size", `${firstCell.offsetWidth}px`);
    }
    this.coordsCache = this.gridCells.map((cell) => {
      if (!cell) return { x: 0, y: 0 };
      return { x: cell.offsetLeft, y: cell.offsetTop };
    });
  }

  private flushPendingSettle() {
    if (this.pendingSettleTimer !== null) {
      clearTimeout(this.pendingSettleTimer);
      this.pendingSettleTimer = null;
    }
    if (this.pendingSettleFn) {
      const fn = this.pendingSettleFn;
      this.pendingSettleFn = null;
      fn();
    }
  }

  public syncRivalTiles(tiles: TileData[]) {
    this.flushPendingSettle();
    this.updateCoordsCache();
    const incomingIds = new Set(tiles.map((t) => t.id));

    // 收集本帧由两个旧方块合并生成的新方块，以便让两个旧方块先沿真实滑动方向滑入目标格！
    const mergeTargetByOldId = new Map<number, { x: number; y: number }>();
    for (const t of tiles) {
      if (!this.tileMap.has(t.id) && t.mergedIds) {
        const idx = t.r * 4 + t.c;
        const coords = this.coordsCache[idx] || { x: 0, y: 0 };
        mergeTargetByOldId.set(t.mergedIds[0], coords);
        mergeTargetByOldId.set(t.mergedIds[1], coords);
      }
    }

    const slidingOldEls: HTMLElement[] = [];

    // 1. 处理消失的方块：若属于合成源方块，则平滑滑入合成目标格；否则直接移除（绝不重置外层 transform 导致向左上/右上乱飞）
    for (const [id, item] of this.tileMap.entries()) {
      if (!incomingIds.has(id)) {
        const mergeTarget = mergeTargetByOldId.get(id);
        if (mergeTarget) {
          item.el.style.transform = `translate3d(${mergeTarget.x}px, ${mergeTarget.y}px, 0)`;
          slidingOldEls.push(item.el);
        } else {
          if (item.el.parentNode) item.el.parentNode.removeChild(item.el);
        }
        this.tileMap.delete(id);
      }
    }

    const deferredMerges: Array<{ t: TileData; coords: { x: number; y: number } }> = [];

    // 2. 调度在场方块的平滑位移与新方块的挂载
    for (const t of tiles) {
      const idx = t.r * 4 + t.c;
      const coords = this.coordsCache[idx] || { x: 0, y: 0 };

      if (this.tileMap.has(t.id)) {
        const item = this.tileMap.get(t.id)!;
        item.el.style.transform = `translate3d(${coords.x}px, ${coords.y}px, 0)`;
        item.el.classList.toggle("tile-pending-eject", Boolean(t.isPendingEject));

        if (item.val !== t.val) {
          const oldVal = item.val;
          item.val = t.val;
          const inner = item.el.querySelector(".tile-inner");
          if (inner) {
            const valClass = t.val <= 2048 ? `tile-${t.val}` : "tile-super";
            inner.className = `tile-inner ${valClass}`;
            inner.textContent = String(t.val);
          }
          if (t.val < oldVal) {
            mountTileSplitOverlay(item.el, oldVal);
          }
        }
      } else if (t.mergedIds && slidingOldEls.length > 0) {
        // 合并产生的新方块：等待源方块 85ms 滑入目标格后再原位弹出果冻回弹动效
        deferredMerges.push({ t, coords });
      } else {
        this.mountRivalTile(t, coords, false);
      }
    }

    if (slidingOldEls.length > 0 || deferredMerges.length > 0) {
      this.pendingSettleFn = () => {
        for (const oldEl of slidingOldEls) {
          if (oldEl.parentNode) oldEl.parentNode.removeChild(oldEl);
        }
        for (const { t, coords } of deferredMerges) {
          this.mountRivalTile(t, coords, true);
        }
      };
      this.pendingSettleTimer = window.setTimeout(() => {
        this.pendingSettleTimer = null;
        if (this.pendingSettleFn) {
          const fn = this.pendingSettleFn;
          this.pendingSettleFn = null;
          fn();
        }
      }, 85);
    }
  }

  private mountRivalTile(t: TileData, coords: { x: number; y: number }, isMergedPop: boolean) {
    const el = document.createElement("div");
    el.className = `tile ${isMergedPop || t.isMerged ? "tile-merged" : "tile-new"}${
      t.isPendingEject ? " tile-pending-eject" : ""
    }`;
    el.style.transform = `translate3d(${coords.x}px, ${coords.y}px, 0)`;

    const inner = document.createElement("div");
    const valClass = t.val <= 2048 ? `tile-${t.val}` : "tile-super";
    inner.className = `tile-inner ${valClass}`;
    inner.textContent = String(t.val);
    el.appendChild(inner);

    this.container.appendChild(el);
    this.tileMap.set(t.id, { el, val: t.val });
  }

  public clearAll() {
    this.flushPendingSettle();
    this.container.innerHTML = "";
    this.tileMap.clear();
  }
}

/**
 * 在指定 .tile 节点上生成原方块的左上/右下对角撕裂双残影与高频刀光
 */
function mountTileSplitOverlay(tileEl: HTMLElement, oldVal: number) {
  const oldValClass = oldVal <= 2048 ? `tile-${oldVal}` : "tile-super";
  const fxWrap = document.createElement("div");
  fxWrap.className = "tile-slash-fx-wrap";

  const halfTopLeft = document.createElement("div");
  halfTopLeft.className = `tile-inner ${oldValClass} tile-split-half half-top-left`;
  halfTopLeft.textContent = String(oldVal);

  const halfBottomRight = document.createElement("div");
  halfBottomRight.className = `tile-inner ${oldValClass} tile-split-half half-bottom-right`;
  halfBottomRight.textContent = String(oldVal);

  const bladeBeam = document.createElement("div");
  bladeBeam.className = "slash-blade-beam";

  fxWrap.appendChild(halfTopLeft);
  fxWrap.appendChild(halfBottomRight);
  fxWrap.appendChild(bladeBeam);
  tileEl.appendChild(fxWrap);

  setTimeout(() => {
    if (fxWrap.parentNode) fxWrap.parentNode.removeChild(fxWrap);
  }, 540);
}

